import React, { useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { Plus, Users, Trash2, Camera, Upload, X, User, AlertTriangle, Eye, Pencil } from 'lucide-react';

const RISK_COLORS = { 
  LOW: 'bg-green-100 text-green-700 border-green-300', 
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-300', 
  HIGH: 'bg-orange-100 text-orange-700 border-orange-300', 
  CRITICAL: 'bg-red-100 text-red-700 border-red-300' 
};

const SubjectsTabEnhanced = ({ 
  caseId, 
  subjects, 
  canEdit, 
  isAdmin, 
  token, 
  onRefresh,
  onViewItem,
  onEditItem,
  onDeleteItem
}) => {
  const [uploadingPhoto, setUploadingPhoto] = React.useState(null);
  const fileInputRef = useRef(null);

  const handleDeleteSubject = async (subjectId) => {
    if (onDeleteItem) return onDeleteItem('subject', subjectId, 'Delete this subject?');
    if (!window.confirm('Delete this subject?')) return;
    await fetch(`${BACKEND_URL}/api/subjects/${subjectId}`, { 
      method: 'DELETE', 
      headers: { Authorization: `Bearer ${token}` } 
    });
    onRefresh();
  };

  const handlePhotoUpload = async (subjectId, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Error', description: 'Only JPEG, PNG, WebP, and GIF images are allowed', variant: 'destructive' });
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Error', description: 'Image size must be under 5MB', variant: 'destructive' });
      return;
    }
    
    setUploadingPhoto(subjectId);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${BACKEND_URL}/api/subjects/${subjectId}/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Photo uploaded successfully' });
        onRefresh();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Upload failed');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleDeletePhoto = async (subjectId) => {
    if (!window.confirm('Remove this photo?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/subjects/${subjectId}/photo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast({ title: 'Success', description: 'Photo removed' });
        onRefresh();
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const triggerFileInput = (subjectId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp,image/gif';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) handlePhotoUpload(subjectId, file);
    };
    input.click();
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <Button 
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white" 
            onClick={() => window.location.assign(`/cases/${caseId}/subjects/new`)} 
            data-testid="add-subject-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Person
          </Button>
        </div>
      )}
      
      {subjects.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <h3 className="text-slate-700 font-medium mb-1">No People Tracked</h3>
            <p className="text-slate-500 text-sm">Add subjects, witnesses, or persons of interest to this case</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subjects.map(subject => (
            <Card 
              key={subject.id} 
              className="bg-white border-slate-200 hover:border-[#0F172A]/30 transition-colors overflow-hidden" 
              data-testid={`subject-card-${subject.id}`}
            >
              <CardContent className="p-0">
                {/* Photo Section */}
                <div className="relative bg-slate-100 h-32 flex items-center justify-center">
                  {subject.photo_url ? (
                    <>
                      <img 
                        src={subject.photo_url} 
                        alt={subject.full_name}
                        className="w-full h-full object-cover"
                      />
                      {canEdit && (
                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            onClick={() => triggerFileInput(subject.id)}
                            className="p-1.5 bg-white/90 rounded-full shadow hover:bg-white"
                            title="Change photo"
                          >
                            <Camera className="w-4 h-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => handleDeletePhoto(subject.id)}
                            className="p-1.5 bg-white/90 rounded-full shadow hover:bg-white"
                            title="Remove photo"
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center">
                      <User className="w-12 h-12 text-slate-300" />
                      {canEdit && (
                        <button
                          onClick={() => triggerFileInput(subject.id)}
                          disabled={uploadingPhoto === subject.id}
                          className="mt-2 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 bg-white px-2 py-1 rounded shadow-sm"
                        >
                          {uploadingPhoto === subject.id ? (
                            <span className="animate-pulse">Uploading...</span>
                          ) : (
                            <>
                              <Upload className="w-3 h-3" /> Add Photo
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Role Badge */}
                  {subject.role_in_case && subject.role_in_case !== 'SUBJECT' && (
                    <Badge className="absolute top-2 left-2 bg-[#0F172A] text-white text-xs">
                      {subject.role_in_case?.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </div>
                
                {/* Info Section */}
                <div 
                  className="p-4 cursor-pointer" 
                  onClick={() => onViewItem(subject, 'subject')}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-slate-900 font-semibold">{subject.full_name}</h3>
                      {subject.aliases && subject.aliases.length > 0 && (
                        <p className="text-xs text-slate-500">
                          AKA: {Array.isArray(subject.aliases) ? subject.aliases.join(', ') : subject.aliases}
                        </p>
                      )}
                    </div>
                    <Badge className={`${RISK_COLORS[subject.surveillance_risk_level] || RISK_COLORS.MEDIUM} text-xs border`}>
                      {subject.surveillance_risk_level}
                    </Badge>
                  </div>
                  
                  {/* Caution Indicators */}
                  {subject.caution_indicators && (
                    <div className="flex items-center gap-1 mb-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                      <AlertTriangle className="w-3 h-3" />
                      <span className="truncate">{subject.caution_indicators}</span>
                    </div>
                  )}
                  
                  <div className="space-y-1 text-sm">
                    {subject.date_of_birth && (
                      <div className="flex gap-2">
                        <span className="text-slate-500 w-12">DOB:</span>
                        <span className="text-slate-700">{subject.date_of_birth}</span>
                      </div>
                    )}
                    {subject.gender && (
                      <div className="flex gap-2">
                        <span className="text-slate-500 w-12">Sex:</span>
                        <span className="text-slate-700">{subject.gender}</span>
                      </div>
                    )}
                    {subject.last_known_address && (
                      <div className="flex gap-2">
                        <span className="text-slate-500 w-12">Addr:</span>
                        <span className="text-slate-700 truncate">{subject.last_known_address}</span>
                      </div>
                    )}
                    {subject.employer && (
                      <div className="flex gap-2">
                        <span className="text-slate-500 w-12">Work:</span>
                        <span className="text-slate-700">{subject.employer}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="border-t border-slate-100 px-4 py-2 flex flex-wrap gap-2 justify-end">
                  <Button
                    size="sm" variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    onClick={(e) => { e.stopPropagation(); onViewItem(subject, 'subject'); }}
                    data-testid={`subject-view-${subject.id}`}
                    title="View details"
                  >
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  {canEdit && (
                    <Button
                      size="sm" variant="outline"
                      className="border-slate-300 text-slate-700 hover:bg-slate-100"
                      onClick={(e) => { e.stopPropagation(); (onEditItem ? onEditItem(subject, 'subject') : onViewItem(subject, 'subject')); }}
                      data-testid={`subject-edit-${subject.id}`}
                      title="Edit subject"
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      size="sm" variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); handleDeleteSubject(subject.id); }}
                      data-testid={`subject-delete-${subject.id}`}
                      title="Delete subject"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Subject dialog removed — "Add Person" button routes to
          /cases/:caseId/subjects/new (SubjectFormPage). View / Edit rows also
          route via parent onViewItem/onEditItem handlers. */}
    </div>
  );
};

export default SubjectsTabEnhanced;
